import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { todayISO } from '@/lib/format';
import { useAuthStore } from './auth.store';
import type { Product } from '@/types/product';
import type { Slot } from '@/types/product.slot';
import type { Bundle, CouponCartItem, CouponValidation } from '@/types/coupon';
import type { PromoGroupType, SourceChannel, SaleIdentity, DocumentScanResult } from '@/types/sale';
import type { SeatItinerary } from '@/types/itinerary';
import type { CartMode } from '@/lib/products';
import { itemFromBundle, itemFromLine, reportPosEvent } from '@/lib/posEvents';
import { slotPriceKey } from '@/lib/slotPricing';

export type { CartMode };

/** The FOC supporting document attached to the cart (from POST /foc-document). */
export interface FocDocument {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  /**
   * Local object URL for an image preview (png/jpg only). Display-only; owned
   * by the store, which revokes it when the document is replaced or cleared.
   */
  previewUrl?: string;
}

export interface CartLineAddOn {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  slotId?: string;
}

/**
 * Per-ticket ("seat") configuration. Each unit of a product line gets its own
 * slot, add-ons and visitor label — mirroring the online FE's per-seat
 * add-on/slot step (app-pos-tiket-online /addon-ticket).
 */
export interface LineSeat {
  slotId?: string;
  slotLabel?: string;
  /**
   * This ticket's price in its picked slot, when a time-of-day rate prices
   * that slot differently from the line's date price. Unset → `line.unitPrice`.
   * Display estimate like `unitPrice`; cleared whenever the slot changes.
   */
  unitPrice?: number;
  addOns: CartLineAddOn[];
  label?: string;
  /**
   * The Smart Itinerary plan + timing config the cashier applied to this seat's
   * wahana (Atur Jadwal Pintar). Rides onto the matching `/sale` item so it is
   * stamped on the minted wristband's `qrCode.itinerary`. No pricing impact.
   */
  itinerary?: SeatItinerary;
}

export interface CartLine {
  /** Stable client key — the productId (one line per product; slots live per-seat). */
  key: string;
  productId: string;
  name: string;
  /** Server-resolved unit price (display estimate; reconciled by BE on sale/quote). */
  unitPrice: number;
  category?: string;
  /** Whether each seat must pick a time slot before checkout. */
  requiresSlot: boolean;
  /** Whether seats can attach add-ons. */
  hasAddOns: boolean;
  /** One entry per ticket; length === quantity. */
  seats: LineSeat[];
}

/** A bundle in the cart. Bundle composition/pricing is reconciled server-side;
 *  for FLEXIBLE bundles we carry the customer's picks in `selectedItems`. */
export interface BundleLine {
  /** Stable client key — the bundleId. */
  key: string;
  bundleId: string;
  name: string;
  unitPrice: number;
  quantity: number;
  bundleType: string;
  /** Number of component tickets (display only). */
  itemCount: number;
  /** FLEXIBLE bundles only. */
  selectedItems?: Array<{ productId: string; quantity: number; slotId?: string }>;
  /**
   * Slot-required components + the cashier's slot pick for each. The backend
   * requires a slot for every `requiresTimeSlot` component (sent as
   * `componentSlots` on the sale); a bundle with any unpicked component can't
   * be sold. Empty when the bundle has no slot-required components.
   */
  components: Array<{ productId: string; name: string; slotId?: string }>;
  /**
   * Extra attraction tickets the cashier attaches ON TOP of the bundle's fixed
   * components — each charged at its own rate (sent as bundleItems[].addOnItems
   * with bundleQuantity:0). Each is a distinct quantity-1 entry with its own
   * slot, mirroring the per-seat attraction children on individual tickets.
   */
  extraAddOns: CartLineAddOn[];
}

interface CartState {
  lines: CartLine[];
  bundleLines: BundleLine[];
  customer: { name: string; phone: string; memberId?: string };
  visitDate: string;
  couponCode: string | null;
  couponResult: CouponValidation | null;
  discountIds: string[];
  /**
   * Unified promo-scheme context (v.1.0.22). Cashier-set signals that narrow
   * which promos apply: group segment, sales channel, KTP attestation. All
   * optional; a plain sale leaves them null.
   */
  promo: {
    groupType: PromoGroupType | null;
    sourceChannel: SourceChannel | null;
    identity: SaleIdentity | null;
    /**
     * Document-OCR scan result (v.1.0.24). `documentScan` is the display
     * snapshot (extracted fields for cashier confirmation + the eligible-preview
     * context); `documentScanToken` is the single-use token the sale carries so
     * the server re-reads the trusted extraction and gates document-OCR promos.
     */
    documentScan: DocumentScanResult | null;
    documentScanToken: string | null;
  };
  /**
   * Which flow this cart belongs to. Regular and free-of-charge (FOC) tickets
   * never share a cart: the catalog pages switch the mode while the cart is
   * empty and ask to clear it otherwise. `clearCart` returns to 'regular'.
   */
  mode: CartMode;
  /** FOC mode only — the uploaded supporting document the sale will carry. */
  focDocument: FocDocument | null;

  addProduct: (product: Product, opts?: { quantity?: number; unitPrice?: number }) => void;
  addBundle: (
    bundle: Bundle,
    opts?: { quantity?: number; unitPrice?: number; selectedItems?: BundleLine['selectedItems'] },
  ) => void;
  /**
   * Re-price every cart line, seat add-on, bundle, and bundle extra from
   * date-resolved price maps (keyed by product / bundle id). Cart prices are
   * display-only — the BE re-resolves authoritatively at sale time — so this
   * just keeps the shown subtotal in sync with the eventual charge (e.g. after
   * the cashier changes the visit date). Ids absent from a map keep their
   * current price.
   *
   * `slotPrices` (keyed by `slotPriceKey`) prices anything with a picked slot
   * against that slot. Omitted while it loads: slotted seats and add-ons keep
   * their current price rather than flashing back to the date price. A pair
   * missing from a loaded map falls back to the date price.
   */
  repriceCart: (
    productPrices: Record<string, number>,
    bundlePrices: Record<string, { price: number }>,
    slotPrices?: Record<string, number>,
  ) => void;
  updateBundleQty: (key: string, quantity: number) => void;
  removeBundle: (key: string) => void;
  /** Set the slot pick for one slot-required bundle component. */
  setBundleComponentSlot: (key: string, productId: string, slot: Slot | undefined) => void;
  /** Replace the extra attraction add-ons attached to a bundle. */
  setBundleAddOns: (key: string, addOns: CartLineAddOn[]) => void;
  /** Resize a line's seat list (grow with blank seats, shrink from the end). */
  updateQty: (key: string, quantity: number) => void;
  removeLine: (key: string) => void;

  // Per-seat configuration (Add-ons & Slots step).
  setSeatSlot: (key: string, seatIndex: number, slot: Slot | undefined) => void;
  setSeatAddOns: (key: string, seatIndex: number, addOns: CartLineAddOn[]) => void;
  setSeatLabel: (key: string, seatIndex: number, label: string) => void;
  /** Persist the applied Smart Itinerary (plan + config) for one seat. */
  setSeatItinerary: (key: string, seatIndex: number, itinerary: SeatItinerary) => void;
  /** Copy one seat's slot + add-ons to every seat in the line (Terapkan ke semua). */
  applySeatToAll: (key: string, seatIndex: number) => void;

  setUnitPrice: (key: string, unitPrice: number) => void;
  setCustomer: (customer: Partial<CartState['customer']>) => void;
  setVisitDate: (date: string) => void;
  applyCoupon: (result: CouponValidation | null) => void;
  toggleDiscount: (id: string) => void;
  setPromo: (patch: Partial<CartState['promo']>) => void;
  /** Switch flow. Callers only do this on an empty cart (or after clearing it). */
  setMode: (mode: CartMode) => void;
  setFocDocument: (doc: FocDocument | null) => void;
  /**
   * Empty the cart. A cashier-initiated clear is logged (POST /sale/pos-events,
   * fire-and-forget); pass `{ reason: 'checkout' }` for the reset after a
   * completed sale, which is not a removal and is not logged.
   */
  clearCart: (opts?: { reason?: 'checkout' }) => void;
}

/** Release a replaced/cleared document's preview object URL. */
function revokePreview(doc: FocDocument | null | undefined, keep?: FocDocument | null) {
  if (!doc?.previewUrl || doc.previewUrl === keep?.previewUrl) return;
  try {
    URL.revokeObjectURL(doc.previewUrl);
  } catch {
    /* not an object URL / no DOM — nothing to release */
  }
}

const EMPTY_PROMO: CartState['promo'] = {
  groupType: null,
  sourceChannel: null,
  identity: null,
  documentScan: null,
  documentScanToken: null,
};

const EMPTY_CUSTOMER = { name: '', phone: '', memberId: undefined };

const blankSeat = (): LineSeat => ({ addOns: [] });

/** Grow/shrink a seat list to `quantity`, preserving existing seat config. */
function resizeSeats(seats: LineSeat[], quantity: number): LineSeat[] {
  if (quantity <= seats.length) return seats.slice(0, Math.max(0, quantity));
  return [...seats, ...Array.from({ length: quantity - seats.length }, blankSeat)];
}

function seatAddOnTotal(seat: LineSeat): number {
  return seat.addOns.reduce((s, a) => s + a.price * a.quantity, 0);
}

/** One ticket's price: its slot price when it has one, else the line's date price. */
function seatUnitPrice(line: CartLine, seat: LineSeat): number {
  return typeof seat.unitPrice === 'number' ? seat.unitPrice : line.unitPrice;
}

/** The line's tickets alone (no add-ons) — replaces `unitPrice × seats`. */
function lineTicketTotal(line: CartLine): number {
  return line.seats.reduce((sum, seat) => sum + seatUnitPrice(line, seat), 0);
}

/**
 * The line's tickets as cart items, one per (product, price): seats of one line
 * in differently priced slots keep their own price. A line with one price is
 * one item, as before.
 */
function lineTicketItems(line: CartLine): CouponCartItem[] {
  const byPrice = new Map<number, number>();
  for (const seat of line.seats) {
    const p = seatUnitPrice(line, seat);
    byPrice.set(p, (byPrice.get(p) ?? 0) + 1);
  }
  return [...byPrice].map(([price, quantity]) => ({
    productId: line.productId,
    quantity,
    price,
    subtotal: price * quantity,
  }));
}

function lineSubtotal(line: CartLine): number {
  return line.seats.reduce(
    (sum, seat) => sum + seatUnitPrice(line, seat) + seatAddOnTotal(seat),
    0,
  );
}

function bundleLineSubtotal(line: BundleLine): number {
  const extras = (line.extraAddOns ?? []).reduce((s, a) => s + a.price * a.quantity, 0);
  return line.unitPrice * line.quantity + extras;
}

/** Map a seat config within a specific line, returning a new lines array. */
function mapSeat(
  lines: CartLine[],
  key: string,
  seatIndex: number,
  fn: (seat: LineSeat) => LineSeat,
): CartLine[] {
  return lines.map((l) =>
    l.key === key
      ? { ...l, seats: l.seats.map((seat, i) => (i === seatIndex ? fn(seat) : seat)) }
      : l,
  );
}

/**
 * E.01 — the pre-pay cart survives a page refresh.
 *
 * Before this the cart was pure in-memory state: a cashier who reloaded (or
 * whose tab crashed) half-way through a 20-ticket queue lost every line and
 * started over with the customer still standing there.
 *
 * `sessionStorage`, NOT `localStorage`, and that is the whole design:
 *   - The cart belongs to this TILL SESSION, not to the browser profile. Two
 *     cashiers taking turns at one machine must not inherit each other's cart,
 *     and `localStorage` would hand it over silently.
 *   - It dies with the tab, which is the correct lifetime for an unpaid cart.
 *
 * Two guards on top of that, because "restored the wrong cart" is worse than
 * "lost the cart":
 *   - `sessionId` — the login session the cart was built in. A different
 *     cashier (or the same one after a re-login) starts empty.
 *   - `storedOn` — the Asia/Jakarta civil day. A cart left over from yesterday
 *     belongs to a shift that has closed; restoring it would quietly carry
 *     yesterday's visit date into today's till.
 *
 * Three things are deliberately NOT persisted:
 *   - `focDocument` — its `previewUrl` is an object URL that is dead after a
 *     reload, and a free-of-charge supporting document silently surviving a
 *     refresh the cashier did not intend is a compliance problem, not a
 *     convenience.
 *   - `promo.identity` (KTP attestation) and `promo.documentScan` /
 *     `documentScanToken` (the OCR extraction + its single-use token). These
 *     are attestations about the person at the counter, cheap to redo and
 *     wrong to assume; the server re-reads the trusted extraction from the
 *     token, so a stale one must not ride along.
 * Everything else — lines, seats, bundles, customer, visit date, coupon,
 * selected discounts, the group/channel picks and the cart mode — is restored.
 */
interface PersistedCart {
  lines: CartLine[];
  bundleLines: BundleLine[];
  customer: CartState['customer'];
  visitDate: string;
  couponCode: string | null;
  couponResult: CouponValidation | null;
  discountIds: string[];
  mode: CartMode;
  /** Only the two plain cashier picks; attestations are never persisted. */
  promo: { groupType: PromoGroupType | null; sourceChannel: SourceChannel | null };
  /** Asia/Jakarta civil day the cart was last written on. */
  storedOn: string;
  /** The login session the cart was built in. */
  sessionId: string | null;
}

const CART_STORAGE_KEY = 'ticketbox_cart';

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      bundleLines: [],
      customer: { ...EMPTY_CUSTOMER },
      visitDate: todayISO(),
      couponCode: null,
      couponResult: null,
      discountIds: [],
      promo: { ...EMPTY_PROMO },
      mode: 'regular',
      focDocument: null,

      addProduct: (product, opts) =>
        set((state) => {
          const qty = opts?.quantity ?? 1;
          const key = product.id;
          const existing = state.lines.find((l) => l.key === key);
          // Prefer the date-resolved price supplied by the caller; fall back to the
          // product's discounted/base catalog price.
          const unitPrice =
            typeof opts?.unitPrice === 'number'
              ? opts.unitPrice
              : product.isDiscountable && typeof product.discountedPrice === 'number'
                ? product.discountedPrice
                : product.price;

          if (existing) {
            return {
              lines: state.lines.map((l) =>
                l.key === key ? { ...l, seats: resizeSeats(l.seats, l.seats.length + qty) } : l,
              ),
            };
          }

          const newLine: CartLine = {
            key,
            productId: product.id,
            name: product.name,
            unitPrice,
            category: product.category,
            requiresSlot: Boolean(product.requiresSlot),
            hasAddOns: Boolean(product.hasAddOns),
            seats: Array.from({ length: qty }, blankSeat),
          };
          return { lines: [...state.lines, newLine] };
        }),

      addBundle: (bundle, opts) =>
        set((state) => {
          const qty = opts?.quantity ?? 1;
          const existing = state.bundleLines.find((b) => b.key === bundle.id);
          if (existing) {
            return {
              bundleLines: state.bundleLines.map((b) =>
                b.key === bundle.id ? { ...b, quantity: b.quantity + qty } : b,
              ),
            };
          }
          const newLine: BundleLine = {
            key: bundle.id,
            bundleId: bundle.id,
            name: bundle.name,
            unitPrice: typeof opts?.unitPrice === 'number' ? opts.unitPrice : bundle.price,
            quantity: qty,
            bundleType: bundle.bundleType,
            itemCount: bundle.items.reduce((s, it) => s + (it.quantity ?? 1), 0),
            selectedItems: opts?.selectedItems,
            // Each slot-required component needs its own time pick before checkout.
            components: bundle.items
              .filter((it) => it.requiresTimeSlot)
              .map((it) => ({ productId: it.productId, name: it.name ?? 'Komponen', slotId: undefined })),
            extraAddOns: [],
          };
          return { bundleLines: [...state.bundleLines, newLine] };
        }),

      updateBundleQty: (key, quantity) => {
        // The stepper reaching 0 removes the bundle — logged like a removal.
        const removed = quantity <= 0 ? get().bundleLines.find((b) => b.key === key) : undefined;
        set((state) => ({
          bundleLines:
            quantity <= 0
              ? state.bundleLines.filter((b) => b.key !== key)
              : state.bundleLines.map((b) => (b.key === key ? { ...b, quantity } : b)),
        }));
        if (removed) reportPosEvent('CART_ITEM_REMOVED', [itemFromBundle(removed)]);
      },

      removeBundle: (key) => {
        const removed = get().bundleLines.find((b) => b.key === key);
        set((state) => ({ bundleLines: state.bundleLines.filter((b) => b.key !== key) }));
        if (removed) reportPosEvent('CART_ITEM_REMOVED', [itemFromBundle(removed)]);
      },

      setBundleComponentSlot: (key, productId, slot) =>
        set((state) => ({
          bundleLines: state.bundleLines.map((b) =>
            b.key === key
              ? {
                  ...b,
                  components: b.components.map((c) =>
                    c.productId === productId ? { ...c, slotId: slot?.id } : c,
                  ),
                }
              : b,
          ),
        })),

      setBundleAddOns: (key, addOns) =>
        set((state) => ({
          bundleLines: state.bundleLines.map((b) =>
            b.key === key ? { ...b, extraAddOns: addOns } : b,
          ),
        })),

      repriceCart: (productPrices, bundlePrices, slotPrices) =>
        set((state) => {
          const priceOf = (productId: string, current: number) =>
            typeof productPrices[productId] === 'number' ? productPrices[productId] : current;
          // undefined → no slot price known (yet); the caller decides the fallback.
          const slotPriceOf = (productId: string, slotId: string | undefined) => {
            if (!slotId || !slotPrices) return undefined;
            const p = slotPrices[slotPriceKey(productId, slotId)];
            return typeof p === 'number' ? p : undefined;
          };
          // An add-on with a slot: its slot price; while slot prices load, keep
          // what it shows; once loaded and still unknown, the date price.
          const addOnPrice = (a: CartLineAddOn) => {
            const sp = slotPriceOf(a.productId, a.slotId);
            if (sp !== undefined) return sp;
            if (a.slotId && !slotPrices) return a.price;
            return priceOf(a.productId, a.price);
          };

          const lines = state.lines.map((l) => ({
            ...l,
            unitPrice: priceOf(l.productId, l.unitPrice),
            seats: l.seats.map((seat) => {
              const sp = slotPriceOf(l.productId, seat.slotId);
              return {
                ...seat,
                unitPrice:
                  sp !== undefined ? sp : seat.slotId && !slotPrices ? seat.unitPrice : undefined,
                addOns: seat.addOns.map((a) => ({ ...a, price: addOnPrice(a) })),
              };
            }),
          }));

          const bundleLines = state.bundleLines.map((b) => {
            const bp = bundlePrices[b.bundleId];
            return {
              ...b,
              unitPrice: typeof bp?.price === 'number' ? bp.price : b.unitPrice,
              extraAddOns: b.extraAddOns.map((a) => ({ ...a, price: addOnPrice(a) })),
            };
          });

          return { lines, bundleLines };
        }),

      updateQty: (key, quantity) => {
        // The stepper reaching 0 removes the line — logged like a removal.
        const removed = quantity <= 0 ? get().lines.find((l) => l.key === key) : undefined;
        set((state) => ({
          lines:
            quantity <= 0
              ? state.lines.filter((l) => l.key !== key)
              : state.lines.map((l) => (l.key === key ? { ...l, seats: resizeSeats(l.seats, quantity) } : l)),
        }));
        if (removed) reportPosEvent('CART_ITEM_REMOVED', [itemFromLine(removed)]);
      },

      removeLine: (key) => {
        const removed = get().lines.find((l) => l.key === key);
        set((state) => ({ lines: state.lines.filter((l) => l.key !== key) }));
        if (removed) reportPosEvent('CART_ITEM_REMOVED', [itemFromLine(removed)]);
      },

      setSeatSlot: (key, seatIndex, slot) =>
        set((state) => ({
          lines: mapSeat(state.lines, key, seatIndex, (seat) => ({
            ...seat,
            slotId: slot?.id,
            slotLabel: slot?.localStart,
            // The old slot's price no longer applies; the slot-price sync re-fills it.
            unitPrice: slot?.id === seat.slotId ? seat.unitPrice : undefined,
          })),
        })),

      setSeatAddOns: (key, seatIndex, addOns) =>
        set((state) => ({
          lines: mapSeat(state.lines, key, seatIndex, (seat) => ({ ...seat, addOns })),
        })),

      setSeatLabel: (key, seatIndex, label) =>
        set((state) => ({
          lines: mapSeat(state.lines, key, seatIndex, (seat) => ({ ...seat, label })),
        })),

      setSeatItinerary: (key, seatIndex, itinerary) =>
        set((state) => ({
          lines: mapSeat(state.lines, key, seatIndex, (seat) => ({ ...seat, itinerary })),
        })),

      applySeatToAll: (key, seatIndex) =>
        set((state) => ({
          lines: state.lines.map((l) => {
            if (l.key !== key) return l;
            const src = l.seats[seatIndex];
            if (!src) return l;
            return {
              ...l,
              seats: l.seats.map((seat) => ({
                ...seat,
                slotId: src.slotId,
                slotLabel: src.slotLabel,
                unitPrice: src.unitPrice,
                addOns: src.addOns.map((a) => ({ ...a })),
              })),
            };
          }),
        })),

      setUnitPrice: (key, unitPrice) =>
        set((state) => ({
          lines: state.lines.map((l) => (l.key === key ? { ...l, unitPrice } : l)),
        })),

      setCustomer: (customer) =>
        set((state) => ({ customer: { ...state.customer, ...customer } })),

      // A coupon was checked against the old day. A discount can be limited to
      // visit dates, so on a new day it has to be entered again rather than keep
      // showing a saving the sale may refuse.
      setVisitDate: (date) =>
        set((state) =>
          state.visitDate === date
            ? {}
            : { visitDate: date, couponCode: null, couponResult: null },
        ),

      applyCoupon: (result) =>
        set({ couponResult: result, couponCode: result?.valid ? result.code : null }),

      toggleDiscount: (id) =>
        set((state) => ({
          discountIds: state.discountIds.includes(id)
            ? state.discountIds.filter((d) => d !== id)
            : [...state.discountIds, id],
        })),

      setPromo: (patch) =>
        set((state) => ({ promo: { ...state.promo, ...patch } })),

      setMode: (mode) =>
        set((state) => {
          if (state.mode === mode) return {};
          // A document belongs to one FOC cart — leaving the FOC flow drops it.
          revokePreview(state.focDocument);
          return { mode, focDocument: null };
        }),

      setFocDocument: (doc) =>
        set((state) => {
          revokePreview(state.focDocument, doc);
          return { focDocument: doc };
        }),

      clearCart: (opts) => {
        const { lines, bundleLines } = get();
        const cleared =
          opts?.reason === 'checkout'
            ? []
            : [...lines.map(itemFromLine), ...bundleLines.map(itemFromBundle)];
        set((state) => {
          revokePreview(state.focDocument);
          return {
            lines: [],
            bundleLines: [],
            customer: { ...EMPTY_CUSTOMER },
            couponCode: null,
            couponResult: null,
            discountIds: [],
            promo: { ...EMPTY_PROMO },
            mode: 'regular',
            focDocument: null,
          };
        });
        if (cleared.length) reportPosEvent('CART_CLEARED', cleared);
      },
    }),
    {
      name: CART_STORAGE_KEY,
      // sessionStorage, not the persist default (localStorage) — see the note
      // on PersistedCart. The cart is till-session state, not profile state.
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s): PersistedCart => ({
        lines: s.lines,
        bundleLines: s.bundleLines,
        customer: s.customer,
        visitDate: s.visitDate,
        couponCode: s.couponCode,
        couponResult: s.couponResult,
        discountIds: s.discountIds,
        mode: s.mode,
        promo: { groupType: s.promo.groupType, sourceChannel: s.promo.sourceChannel },
        storedOn: todayISO(),
        sessionId: useAuthStore.getState().sessionId ?? null,
      }),
      /**
       * Restore only a cart this cashier built today. Anything else is dropped
       * in favour of a clean cart — the default `merge` would shallow-spread it
       * in regardless, which is how a stale cart reaches a till.
       *
       * Fields are copied one by one rather than spread, so a persisted blob
       * from an older build (or a hand-edited one) can never inject keys that
       * are not part of the cart — including the action functions.
       */
      merge: (persisted, current): CartState => {
        const p = persisted as Partial<PersistedCart> | undefined;
        if (!p) return current;
        if (p.storedOn !== todayISO()) return current;
        if ((p.sessionId ?? null) !== (useAuthStore.getState().sessionId ?? null)) return current;
        return {
          ...current,
          lines: p.lines ?? [],
          bundleLines: p.bundleLines ?? [],
          customer: { ...EMPTY_CUSTOMER, ...(p.customer ?? {}) },
          visitDate: p.visitDate ?? current.visitDate,
          couponCode: p.couponCode ?? null,
          couponResult: p.couponResult ?? null,
          discountIds: p.discountIds ?? [],
          mode: p.mode ?? 'regular',
          promo: {
            ...EMPTY_PROMO,
            groupType: p.promo?.groupType ?? null,
            sourceChannel: p.promo?.sourceChannel ?? null,
          },
          // Never restored — see the note on PersistedCart.
          focDocument: null,
        };
      },
    },
  ),
);

/**
 * A till handover must not carry a cart across it.
 *
 * The persisted blob is guarded at REHYDRATION (same civil day, same session),
 * but a logout in the same tab never reloads the page: the store stays in
 * memory, so the next cashier inherits the previous one's lines — and their
 * first edit re-stamps the blob with their own session id, laundering it past
 * the guard it was supposed to fail. Clear on every session change, which
 * covers logout, a re-login, and the 401 path in `lib/axios.ts`.
 */
useAuthStore.subscribe((state, prev) => {
  if (state.sessionId === prev.sessionId) return;
  useCartStore.setState((s) => {
    revokePreview(s.focDocument);
    return {
      lines: [],
      bundleLines: [],
      customer: { ...EMPTY_CUSTOMER },
      couponCode: null,
      couponResult: null,
      discountIds: [],
      promo: { ...EMPTY_PROMO },
      mode: 'regular',
      focDocument: null,
    };
  });
  // Drop the blob too, so a reload cannot resurrect it either.
  try {
    sessionStorage.removeItem(CART_STORAGE_KEY);
  } catch {
    /* private mode / storage disabled — the in-memory reset above still holds */
  }
});

// ─── Selectors (subscribe narrowly) ────────────────────────────────────────
export const cartSelectors = {
  subtotal: (s: CartState): number =>
    s.lines.reduce((sum, l) => sum + lineSubtotal(l), 0) +
    s.bundleLines.reduce((sum, b) => sum + bundleLineSubtotal(b), 0),
  itemCount: (s: CartState): number =>
    s.lines.reduce((sum, l) => sum + l.seats.length, 0) +
    s.bundleLines.reduce((sum, b) => sum + b.quantity, 0),
  /** Total distinct lines (products + bundles) — for empty-cart guards. */
  lineCount: (s: CartState): number => s.lines.length + s.bundleLines.length,
  couponSavings: (s: CartState): number =>
    s.couponResult?.valid ? (s.couponResult.discountAmount ?? 0) : 0,
  /**
   * The cart as POST /coupon/apply wants it. The endpoint computes the saving
   * FROM this, so it has to be sent — a coupon applied against no cart gets no
   * amount back.
   *
   * Product lines only: a bundle has no `productId`, and passing a bundleId as
   * one would make a product-scoped discount match the wrong thing.
   * `totalAmount` still carries the FULL subtotal (bundles included), because
   * that is what a cart-scope percentage and `minPurchaseAmount` are measured
   * against.
   */
  /**
   * The cart posted to /coupon/apply. Add-ons are products the engine matches by
   * productId, so each one is its own item and the ticket line carries only the
   * ticket's price. Folding them into the ticket line made a coupon for an
   * add-on worth Rp 0, which reads as "Kupon tidak berlaku".
   */
  couponCart: (s: CartState): { items: CouponCartItem[]; totalAmount: number } => {
    const items: CouponCartItem[] = s.lines.flatMap(lineTicketItems);
    const addOns = new Map<string, CouponCartItem>();
    const addAddOn = (a: CartLineAddOn) => {
      if (a.quantity <= 0) return;
      const existing = addOns.get(a.productId);
      if (existing) {
        existing.quantity += a.quantity;
        existing.subtotal += a.price * a.quantity;
      } else {
        addOns.set(a.productId, {
          productId: a.productId,
          quantity: a.quantity,
          price: a.price,
          subtotal: a.price * a.quantity,
        });
      }
    };
    s.lines.forEach((l) => l.seats.forEach((seat) => seat.addOns.forEach(addAddOn)));
    s.bundleLines.forEach((b) => (b.extraAddOns ?? []).forEach(addAddOn));
    return { items: [...items, ...addOns.values()], totalAmount: cartSelectors.subtotal(s) };
  },
  /** Line keys still needing a slot pick on any seat (Add-ons & Slots gate). */
  incompleteLineKeys: (s: CartState): string[] =>
    s.lines
      .filter((l) => l.requiresSlot && l.seats.some((seat) => !seat.slotId))
      .map((l) => l.key),
};

export function selectTotal(s: CartState): number {
  return Math.max(0, cartSelectors.subtotal(s) - cartSelectors.couponSavings(s));
}

export {
  lineSubtotal,
  lineTicketItems,
  lineTicketTotal,
  seatUnitPrice,
  bundleLineSubtotal,
  seatAddOnTotal,
};
